import { useState, type MouseEvent } from "react";
import { Business, BookingProduct, Product } from "@/types/booking";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Minus, Plus, ShoppingBag } from "lucide-react";
import {
  formatPrice,
  getBusinessCurrency,
  isStorefrontProductOutOfStock,
} from "@/lib/utils";

function stockLabel(product: Product) {
  if (isStorefrontProductOutOfStock(product)) return "Out of stock";
  if (typeof product.stockCount === "number" && product.stockCount <= 5) {
    return `Only ${product.stockCount} left`;
  }
  return "In stock";
}

function QuantityStepper({
  quantity,
  atStockLimit,
  onIncrement,
  onDecrement,
  compact = false,
}: {
  quantity: number;
  atStockLimit: boolean;
  onIncrement: () => void;
  onDecrement: () => void;
  compact?: boolean;
}) {
  const buttonClass =
    "h-9 w-9 shrink-0 flex items-center justify-center rounded-md border border-grey-100 text-grey-700 hover:bg-grey-50 disabled:opacity-40 disabled:cursor-not-allowed";

  return (
    <div
      className={`inline-flex w-full items-center justify-between ${
        compact ? "" : "max-w-xs"
      }`}
    >
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onDecrement();
        }}
        className={buttonClass}
        aria-label="Decrease quantity"
      >
        <Minus className="h-4 w-4" />
      </button>
      <span className="px-3 text-sm font-medium text-grey-900 tabular-nums text-center">
        {quantity}
      </span>
      <button
        type="button"
        onClick={(event) => {
          event.stopPropagation();
          onIncrement();
        }}
        disabled={atStockLimit}
        className={buttonClass}
        aria-label="Increase quantity"
      >
        <Plus className="h-4 w-4" />
      </button>
    </div>
  );
}

export default function ProductTile({
  product,
  businessInfo,
  cartItem,
  onAdd,
  onIncrement,
  onDecrement,
  highlighted = false,
  scrollMarginTop,
}: {
  product: Product;
  businessInfo: Business | null;
  cartItem?: BookingProduct;
  onAdd: () => void;
  onIncrement: () => void;
  onDecrement: () => void;
  highlighted?: boolean;
  scrollMarginTop?: number;
}) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const currency = getBusinessCurrency(businessInfo);
  const outOfStock = isStorefrontProductOutOfStock(product);
  const hasImage = Boolean(product.imageUrl?.trim());
  const quantityInCart = cartItem?.quantity || 0;
  const atStockLimit =
    typeof product.stockCount === "number" &&
    quantityInCart >= product.stockCount;
  const price = formatPrice(product.retailPrice, currency);
  const availability = stockLabel(product);
  const categoryName = product.category?.name?.trim();

  const handleAdd = (event?: MouseEvent) => {
    event?.stopPropagation();
    onAdd();
  };

  return (
    <>
      <article
        id={`product-${product.id}`}
        style={
          scrollMarginTop
            ? { scrollMarginTop: `${scrollMarginTop}px` }
            : undefined
        }
        className={`bg-white rounded-lg border border-grey-75 overflow-hidden flex flex-col h-full ${
          highlighted ? "service-deep-link-glow" : ""
        }`}
      >
        <button
          type="button"
          onClick={() => setDetailsOpen(true)}
          className="relative aspect-square bg-grey-50 overflow-hidden group text-left"
          aria-label={`View ${product.name}`}
        >
          {hasImage ? (
            <img
              src={product.imageUrl!}
              alt={product.name}
              className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
            />
          ) : (
            <div className="h-full w-full flex items-center justify-center">
              <ShoppingBag className="h-10 w-10 text-grey-300" />
            </div>
          )}
          {outOfStock && (
            <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
              <span className="text-xs font-semibold uppercase tracking-wide text-grey-700 bg-white px-2.5 py-1 rounded-full">
                Out of stock
              </span>
            </div>
          )}
          {quantityInCart > 0 && !outOfStock && (
            <span className="absolute top-2 right-2 min-w-6 h-6 px-1.5 rounded-full bg-green-300 text-white text-xs font-semibold flex items-center justify-center">
              {quantityInCart}
            </span>
          )}
        </button>

        <div className="p-3 sm:p-4 flex flex-col flex-1 gap-2">
          {categoryName ? (
            <p className="text-[11px] font-medium uppercase tracking-wide text-grey-500 truncate">
              {categoryName}
            </p>
          ) : null}
          <button
            type="button"
            onClick={() => setDetailsOpen(true)}
            className="text-left"
          >
            <h3 className="text-sm font-semibold text-grey-900 font-heading leading-snug line-clamp-2">
              {product.name}
            </h3>
          </button>
          <p className="text-base font-semibold text-green-300">{price}</p>
          <p
            className={`text-xs ${
              outOfStock ? "text-red-600 font-medium" : "text-grey-500"
            }`}
          >
            {availability}
          </p>

          <div className="mt-auto pt-2">
            {outOfStock ? (
              <Button
                disabled
                size="sm"
                className="w-full bg-grey-100 text-grey-400 cursor-not-allowed"
              >
                Out of stock
              </Button>
            ) : quantityInCart > 0 ? (
              <QuantityStepper
                compact
                quantity={quantityInCart}
                atStockLimit={atStockLimit}
                onIncrement={onIncrement}
                onDecrement={onDecrement}
              />
            ) : (
              <Button
                onClick={handleAdd}
                size="sm"
                className="w-full border-green-300 text-green-300 hover:bg-green-300 hover:text-white transition-colors"
                variant="outline"
              >
                Add to cart
              </Button>
            )}
          </div>
        </div>
      </article>

      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="flex flex-col bg-white p-0 gap-0 overflow-hidden sm:max-w-2xl">
          <div className="grid sm:grid-cols-2 min-h-0">
            <div className="relative aspect-square bg-grey-50 overflow-hidden min-w-0">
              {hasImage ? (
                <img
                  src={product.imageUrl!}
                  alt={product.name}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              ) : (
                <div className="absolute inset-0 flex items-center justify-center">
                  <ShoppingBag className="h-14 w-14 text-grey-300" />
                </div>
              )}
            </div>
            <div className="relative z-10 min-w-0 bg-white p-5 sm:p-6 pt-12 sm:pt-6 sm:pr-12 flex flex-col">
              <DialogHeader className="text-left space-y-2">
                {categoryName ? (
                  <p className="text-[11px] font-medium uppercase tracking-wide text-grey-500">
                    {categoryName}
                  </p>
                ) : null}
                <DialogTitle className="text-xl font-heading text-grey-900 leading-snug">
                  {product.name}
                </DialogTitle>
                <p className="text-xl font-semibold text-green-300">{price}</p>
                <DialogDescription
                  className={
                    outOfStock
                      ? "text-red-600 font-medium"
                      : "text-grey-500"
                  }
                >
                  {availability}
                </DialogDescription>
              </DialogHeader>

              {product.description ? (
                <p className="mt-4 text-sm text-grey-600 leading-relaxed">
                  {product.description}
                </p>
              ) : null}

              <div className="mt-auto pt-6">
                {outOfStock ? (
                  <Button
                    disabled
                    className="w-full bg-grey-100 text-grey-400 cursor-not-allowed"
                  >
                    Out of stock
                  </Button>
                ) : quantityInCart > 0 ? (
                  <div className="space-y-3">
                    <QuantityStepper
                      quantity={quantityInCart}
                      atStockLimit={atStockLimit}
                      onIncrement={onIncrement}
                      onDecrement={onDecrement}
                    />
                    <p className="text-sm text-grey-500">
                      {formatPrice(
                        (product.retailPrice || 0) * quantityInCart,
                        currency,
                      )}{" "}
                      in cart
                    </p>
                  </div>
                ) : (
                  <Button
                    onClick={() => onAdd()}
                    className="w-full bg-green-300 text-white hover:bg-green-400"
                  >
                    Add to cart
                  </Button>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
